import React, { useRef, useState, useEffect, useCallback, useMemo, useImperativeHandle } from 'react'
import { dateColumn, keyColumn, textColumn, checkboxColumn, DynamicDataSheetGrid } from 'react-datasheet-grid';
import { useLiveQuery } from 'dexie-react-hooks';
import { tableConfigs } from '../../Utils/Functions/Table_Configs';
import { db, withExtras, withFormulas, withParents, reserveNextTempRecordId } from '../../Utils/Functions/Dexie-Setup';
import SelectColumn from './DatasheetSelect';
import multichoiceColumn from './DatasheetMultichoice';
import { timeColumn } from './DatasheetTime';
import { numberColumn } from './DatasheetNumber';
import { decimalColumn } from './DatasheetDecimal';

const defaultGetDefaultValues = {};//() => ({});
const defaultFilter = () => true;
const defaultColumns = [];
const defaultStyle = {};

// eslint-disable-next-line react/display-name
const DataSheetInput = React.forwardRef(({ tableName, filter = defaultFilter, defaultValues = defaultGetDefaultValues, columns = defaultColumns, label, className = "", style = defaultStyle, ...rest }, ref) => {

    const keyField = useMemo(() => tableConfigs[tableName].fields.find(field => field.type === "key").label, [tableName]);
    const sheetRef = useRef(null);

    const records = useLiveQuery(() => {
        return db[tableName].filter(filter).reverse().toArray(withExtras(tableName, [withParents, withFormulas]))
    }, [tableName, filter]);

    const [data, setData] = useState([]);
    const updatedAt = useRef(0);
    const timeout = useRef("");

    const columnsArray = useMemo(() => columns.map(col => {
        const [fieldName, options] = col

        const field = tableConfigs[tableName].fields.find(field => field.label === fieldName);
        const { inputType, editable, filterFunction, relTo } = field;
        const { displayFields, filter, required } = options;
        const inputsMap = {
            "text": textColumn,
            "time": timeColumn,
            "number": numberColumn,//intColumn,
            "currency": decimalColumn,
            "date": dateColumn,
            "checkbox": checkboxColumn,
            "relationship": SelectColumn({ tableName: relTo, filter: (filter || filterFunction), displayFields, required, sheetRef: sheetRef}),
            "text-multiline": textColumn,
            "text-multichoice": multichoiceColumn({ tableName, fieldName }),
        };
        return {
            ...keyColumn(fieldName, inputsMap[inputType]),
            ...options,
            disabled: options.disabled || !editable
        }
    }), [columns, tableName])

    const genId = useCallback(() => {
        return reserveNextTempRecordId(tableName);
    }, [tableName])


    const onChange = useCallback((changedData, operations) => {
        updatedAt.current = Date.now();
        const locallyUpdatedAt = Date.now() + 150;
        for (const operation of operations) {
            switch (operation.type) {
                case "DELETE":
                    const deletedKeys = [];

                    data.slice(operation.fromRowIndex, operation.toRowIndex).forEach(row => {
                        deletedKeys.push(row[keyField]);
                    })
                    // console.log(deletedKeys);
                    db[tableName].bulkDelete(deletedKeys);
                    break;
                case "UPDATE":
                case "CREATE":
                    changedData.slice(operation.fromRowIndex, operation.toRowIndex).forEach(row => {
                        row.locallyUpdatedAt = locallyUpdatedAt;
                    })
                    break;
                default:
                    console.error("Reached Default Case");
            }
        }
        setData(changedData);
        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            timeout.current = "";

            db[tableName].bulkPut(changedData);
        }, 150);
    }, [keyField, tableName, data])

    const createRow = useCallback(() => {
        const id = genId();
        return {
            ...defaultValues,
            tempId: id,
            [keyField]: id,
        }
    }, [genId, defaultValues, keyField])
    const duplicateRow = useCallback(({ rowData }) => {


        const id = genId();
        return {
            ...rowData,
            ...defaultValues,
            tempId: id,
            [keyField]: id,
        }
    }, [genId, defaultValues, keyField])

    useEffect(() => {
        if (records !== undefined && (records.length !== 0 || data.length !== 0)) {
            if (timeout.current === "") {
                if (!records.length || !updatedAt.current || updatedAt.current < Math.max(...records.map(record => record?.locallyUpdatedAt || 0))) {
                    setData(records ? records : []);
                }
            }
        }
    }, [records, data.length])

    useEffect(() => {
        setData([]);
        updatedAt.current = 0;
        clearTimeout(timeout.current)
        timeout.current = "";
    }, [tableName, filter, defaultValues, columns]);

    useImperativeHandle(ref, () => ({
        forceSave: () => {
            clearTimeout(timeout.current);
            return db[tableName].bulkPut(data);
        }
    }));

    return (
        <div style={{ marginBottom: 20, ...style }} className={'w-full h-full ' + className}>
            <label>{label}</label>
            <DynamicDataSheetGrid
                ref={sheetRef}
                value={data}
                onChange={onChange}
                createRow={createRow}
                duplicateRow={duplicateRow}
                rowKey={keyField}
                columns={columnsArray}
                autoAddRow
                {...rest}
                // style={{height: "100%"}}
                // className='h-max'
            />
        </div>
    )
});

export default DataSheetInput;












